
import ReportRenderer from "@/components/reports/ReportRenderer.vue";
import ElraReport7 from "@/components/reports/ElraReport7.vue";
import ElraReport8 from "@/components/reports/ElraReport8.vue";
import ComparisonRadarChartComponent, { ComparisonAssessmentItem, ComparisonBenchmarkItem } from "@/components/admin/ComparisonComponents/ComparisonRadarChartComponent.vue";
import CommentComponent from "@/components/admin/CommentComponent.vue";
import TopHeader from "@/components/admin/TopHeader.vue";

import { CanadidateReportInfo, TemplateSchema, TypedVue } from "@/store/types";
import { V2Assessment, Benchmark, Project, ProjectTheme, Resource, V2Project } from "@/store/admin/adminTypes";
import { emptyResource } from "@/store/emptyModels";
import { Component, Provide } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { Actions } from "@/store/admin/adminActions";
import VueI18n from "vue-i18n";

import { ColorMap } from "@/lib/charts/HighChartThemes";
import Vue from "vue";
import { Getters } from "@/store/admin/adminGetters";
import { VuexModules } from "@/store";
import { Trait } from "@/lib/consts";
import config from "@/lib/config";

const templates = require.context("@/components/reports/templates", false, /\.html\.tmpl$/); // Uses a method from the webpack require object to get all '.tmpl' files from the templates folder

const namespace = VuexModules.ADMIN;

interface AssessmentAndShortId extends V2Assessment {
  identifier: string;
  shortId: string | undefined;
}

@Component({
  components: {
    ReportRenderer,
    ElraReport7,
    ElraReport8,
    ComparisonRadarChartComponent,
    CommentComponent,
    TopHeader,
  },
})
export default class CandidateReport extends TypedVue {
  @Action(Actions.GET_REPORT, { namespace })
  getReportData!: (args: { assessmentId: string; isCandidate: boolean }) => Promise<CanadidateReportInfo | null>;

  @Action(Actions.GET_ORG_PROJECTS, { namespace })
  getProjects!: () => Promise<V2Project[] | null>;

  @Action(Actions.SET_CURRENT_PROJECT, { namespace })
  setCurrentProject!: (project: V2Project) => void;

  @Action(Actions.GET_CANDIDATE_ASSESSMENT_PROJECT, { namespace })
  getCandidateAssessmentProject!: (assessmentId: string) => Promise<Project | null>;

  @Action(Actions.GET_PROJECT_BENCHMARKS, { namespace })
  getProjectBenchmarks!: (projectId: string) => Promise<Benchmark[]>;

  @Action(Actions.GET_PROJECT_ASSESSMENTS, { namespace })
  getProjectAssessments!: (projectId: string) => Promise<V2Assessment[]>;

  @Action(Actions.GET_CANDIDATES_ASSESSMENT, { namespace })
  getCandidatesAssessment!: (assessmentId: string) => Promise<V2Assessment | null>;

  @Action(Actions.GET_CANDIDATE_ASSESSMENTS, { namespace })
  getCandidateAssessments!: () => Promise<V2Assessment[]>;

  @Action(Actions.GET_TEMPLATE, { namespace })
  getTemplate!: (templateName: string) => Promise<TemplateSchema | null>;

  @Action(Actions.GET_LITERALS, { namespace })
  getLiterals!: ({ module, version }: { module: string; version: string }) => Promise<Resource>;

  @Getter(Getters.GET_CURRENT_PROJECT, { namespace })
  public currentProject!: V2Project | undefined;

  @Getter(Getters.CURRENT_PROJECT_DIVERSITY_ENABLED, { namespace })
  public diveristyEnabled!: boolean;

  @Getter(Getters.CURRENT_PROJECT_FOUR_GROUPS_ENABLED, { namespace })
  public fourGroupsEnabled!: boolean;

  @Getter(Getters.GET_CURRENT_PROJECT_ROOT_BENCHMARK_ID, { namespace })
  public rootBenchmarkId!: string;

  @Getter(Getters.GET_CURRENT_PROJECT_BENCHMARKS, { namespace })
  public projectBenchmarks!: Benchmark[];

  public projects: Project[] = [];

  public comparisonCandidates: ComparisonAssessmentItem[] = Vue.observable([]);
  public comparisonBenchmarks: ComparisonBenchmarkItem[] = Vue.observable([]);

  public reportLoading = true;
  public displayReport = false;
  public showNames = true;
  public showAiInsights = true;
  public candidates: AssessmentAndShortId[] = [];
  public benchmarks: Benchmark[] = [];

  public reportData: CanadidateReportInfo | null = null;
  public candidateView = false;
  public resource: Resource = emptyResource();
  public templateHTML = "";

  // Local i18n provided to descendant report components that opt into per-report literals
  // via @Inject("$i18n"). For v8+ reports we layer report-version-specific literals on top of
  // the global admin dictionary; the local instance shadows $i18n only inside the report subtree.
  @Provide("$i18n") readonly reportI18n: VueI18n = new VueI18n({ locale: "en" });

  public nextCandidate: string | null = null;
  public prevCandidate: string | null = null;

  get projectTheme(): ProjectTheme {
    return (this.reportProject?.theme as ProjectTheme) ?? ProjectTheme.DEFAULT_TA;
  }

  get reportProject(): Project | undefined {
    if (this.currentProject && !this.candidateView) {
      return this.toOldProject(this.currentProject);
    }

    return this.projects.find((project) => project.projectId === this.reportData?.projectId);
  }

  get comparableCandidates(): AssessmentAndShortId[] {
    return this.candidates.filter((candidate) => candidate.reportId !== undefined);
  }

  get currentCandidate(): AssessmentAndShortId | undefined {
    return this.candidates.find((candidate) => candidate.identifier === this.$route.params.id);
  }

  get backHref(): string {
    return `/admin/summary?project=${this.reportProject?.projectId}`;
  }

  get currentCandidateHasTraitScores(): boolean {
    return Object.keys(this.currentCandidate?.report?.traitScores || {}).length > 0;
  }

  get displayComment(): boolean {
    return this.reportData && this.reportData.data.comment && (this.reportData.data.comment.title || this.reportData.data.comment.body);
  }

  get isVersion7Report(): boolean {
    return this.reportData?.type === "elra" && (this.reportData?.version ?? "").startsWith("7");
  }

  get isVersion8Report(): boolean {
    return this.reportData?.type === "elra" && (this.reportData?.version ?? "").startsWith("8");
  }

  get assessmentId(): string {
    return this.$route.params.id;
  }

  get traitClassRanges(): Record<Trait, { low: number; high: number; lowerlow: number; mean: number; standardDeviation: number }> {
    const rootBenchamrk = this.projectBenchmarks.find((b) => b.benchmarkId === this.rootBenchmarkId);
    if (rootBenchamrk && rootBenchamrk.traitCutoffs) {
      return rootBenchamrk.traitCutoffs as Record<Trait, { low: number; high: number; lowerlow: number; mean: number; standardDeviation: number }>;
    }
    return {} as Record<Trait, { low: number; high: number; lowerlow: number; mean: number; standardDeviation: number }>;
  }

  async getTemplateHTML(templateName: string) {
    const template = templates.keys().filter((t) => t.indexOf(templateName) > -1);
    if (template) {
      if (template.length === 0) {
        const templateInfo = await this.getTemplate(templateName);

        if (templateInfo) {
          return templateInfo.template;
        } else {
          return Promise.resolve(`Template ${templateName} not found.`);
        }
      } else if (template.length > 1) {
        return Promise.resolve(`${templateName} has multiple matches.`);
      } else {
        return Promise.resolve(templates(template[0]).default);
      }
    } else {
      return Promise.resolve("Unknown Error Rendering Report.");
    }
  }

  async mounted() {
    const user = await this.$auth.getUser();

    if (!user) {
      await this.$router.push("/");
      return;
    }

    this.candidateView = user.isCandidate || this.$route.query.view === "candidate";

    const reportInfoPromise = this.getReportData({ assessmentId: this.assessmentId, isCandidate: user.isCandidate }).then((info) => {
      this.reportData = info;
    });

    let projectsPromise: Promise<any>;
    if (user.isCandidate) {
      projectsPromise = this.getCandidateAssessmentProject(this.assessmentId).then((p) => (this.projects = p ? [p] : []));
    } else {
      projectsPromise = this.getProjects().then((p) => {
        this.projects = (p ?? []).map((x) => this.toOldProject(x));

        if (this.reportData?.projectId) {
          const currentProject = p?.find((proj) => proj.identifier === this.reportData?.projectId);
          if (currentProject) {
            this.setCurrentProject(currentProject);
          }
        }
      });
    }

    await Promise.all([reportInfoPromise, projectsPromise]);

    if (!this.reportData) {
      // No report for this assessment, so there is nothing further to load
      this.reportLoading = false;
      return;
    }

    if (!this.candidateView && !this.diveristyEnabled && this.reportData) {
      this.reportData.data.diversity = {};
    }
    if (!this.isVersion7Report && !this.isVersion8Report) {
      this.templateHTML = await this.getTemplateHTML(`${this.reportData?.type}_${this.reportData?.version}`);
    }
    // For v7: once strings are migrated out of ElraReport7.vue into literals, uncomment and use version "7"
    // this.resource = await this.getLiterals({ module: `report:${this.reportData?.type}`, version: "7" });
    if (!this.isVersion7Report && !this.isVersion8Report) {
      this.resource = await this.getLiterals({ module: `report:${this.reportData?.type}`, version: (this.reportData?.version ?? "1.0").split(".")[0] });
    }

    if (this.isVersion8Report) {
      const adminMessages = (this.$i18n as VueI18n).getLocaleMessage("en");
      const reportLiterals = await this.getLiterals({ module: `report:${this.reportData?.type}`, version: "8" });
      this.reportI18n.setLocaleMessage("en", { ...adminMessages, ...reportLiterals.dictionary });
    }

    const benchmarksPromise = this.reportProject ? this.getProjectBenchmarks(this.reportProject.projectId) : Promise.resolve([]);
    const [benchmarks, candidateData] = await Promise.all([
      benchmarksPromise,
      user.isCandidate ? this.getOwnAssessmentHistory() : !this.reportProject ? this.getCandidatesAssessment(this.assessmentId).then((x) => [x]) : this.getProjectAssessments(this.reportProject?.projectId),
    ]);

    this.candidates = Vue.observable(
      candidateData.map((entry) => ({
        ...entry,
        shortId: entry?.identifier.substring(0, 4),
        identifier: entry?.identifier || "",
        orgId: entry?.orgId || "",
        projectId: entry?.projectId || "",
        dateCreated: entry?.dateCreated || "",
        attempts: entry?.attempts || 0,
        state: entry?.state || "",
        company: entry?.company || "",
        isDeleted: entry?.isDeleted || false,
        candidate: {
          name: entry?.candidate?.name || "",
          email: entry?.candidate?.email || "",
        },
      })),
    );

    if (!user.isCandidate) {
      const completeCandidates = this.candidates.filter((x) => x.report !== undefined);
      const currentAssessmentIndx = completeCandidates.map((x) => x.identifier).indexOf(this.assessmentId);

      if (completeCandidates.length > 1 && currentAssessmentIndx > -1) {
        this.nextCandidate = completeCandidates[currentAssessmentIndx + 1 === completeCandidates.length ? 0 : currentAssessmentIndx + 1].identifier as string;
        this.prevCandidate = completeCandidates[currentAssessmentIndx - 1 < 0 ? completeCandidates.length - 1 : currentAssessmentIndx - 1].identifier as string;
      }
    }

    this.comparisonCandidates = Vue.observable<ComparisonAssessmentItem[]>(
      this.candidates.map<ComparisonAssessmentItem>((c) => ({
        data: {
          assessmentId: c.identifier,
          project: c.projectId,
          candidateName: c.candidate.name,
          isComplete: c.report !== undefined,
          dateCompleted: c.dateCompleted,
          readinessScore: c.report?.readiness || "default",
          traits: c.report?.traitStatuses || {},
          traitScores: c.report?.traitScores || {},
          competencies: c.report?.competencies || {},
          competencyScores: c.report?.competencyScores || {},
        },
        id: c.identifier,
        hidden: false,
        highlighted: false,
        locked: this.currentCandidate === c,
        selected: this.currentCandidate === c,
        color: localStorage.getItem(`report-chartcolor-${this.currentCandidate?.identifier ?? ""}-${c.identifier}`) ?? ColorMap[this.projectTheme][c.report?.readiness ?? "default"],
        initialColor: ColorMap[this.projectTheme][c.report?.readiness ?? "default"],
      })),
    );

    this.comparisonBenchmarks = benchmarks
      .filter((bench) => {
        if (this.candidateView) {
          if (!bench.isAdmin) {
            return bench;
          }
        } else {
          return bench;
        }
      })
      .map<ComparisonBenchmarkItem>((benchmark) => ({
        data: benchmark,
        id: benchmark.benchmarkId,
        hidden: false,
        highlighted: false,
        selected: this.rootBenchmarkId === benchmark.benchmarkId || false,
        color: localStorage.getItem(`report-chartcolor-${this.currentCandidate?.identifier ?? ""}-${benchmark.benchmarkId}`) ?? benchmark.colour,
        initialColor: benchmark.colour,
        locked: false,
      }));

    this.reportLoading = false;
    this.displayReport = true;

    if (window.userGuiding) {
      window.userGuiding.identify(this.$route.params.id, {
        reportVersion: this.reportData?.version || "Unknown",
        project: this.reportProject?.projectId || "Unknown",
        organization: this.reportProject?.organization || "Unknown",
        reportType: this.reportData?.type || "Unknown",
      });
    }
  }

  // Candidates can compare every completed assessment they have access to (across projects
  // and organizations) with the one being viewed. Falls back to the current assessment alone
  // if the history call fails or does not include it. Current attempt first, then newest first.
  async getOwnAssessmentHistory(): Promise<(V2Assessment | null)[]> {
    const history = await this.getCandidateAssessments().catch(() => [] as V2Assessment[]);
    if (!history.some((a) => a.identifier === this.assessmentId)) {
      const current = await this.getCandidatesAssessment(this.assessmentId);
      if (current) history.unshift(current);
    }
    return history.sort((a, b) => {
      if (a.identifier === this.assessmentId) return -1;
      if (b.identifier === this.assessmentId) return 1;
      return (b.dateCompleted ?? "").localeCompare(a.dateCompleted ?? "");
    });
  }

  print(): void {
    this.$bvModal.hide("print-modal");
    setTimeout(() => {
      window.print();
    }, 500);
  }

  viewGuide(): void {
    if (this.rootBenchmarkId === config.CPA_BENCHMARK_ID) {
      window.open("https://assets.system-3.com/web-assets/report/cpa-report-guide.pdf");
    } else if (this.reportData?.version === "6.1") {
      window.open("https://assets.system-3.com/web-assets/report/report-guide-6.1.pdf");
    } else if (this.reportData?.version === "7.0" || (this.reportData?.version ?? "").startsWith("8")) {
      window.open("https://assets.system-3.com/web-assets/report/report-guide-7.0.pdf");
    } else {
      window.open("https://assets.system-3.com/web-assets/report/report-guide.pdf");
    }
  }

  replayIntro(): void {
    if (window.userGuiding) {
      if (!this.candidateView) {
        window.userGuiding.previewGuide(Number(this.$config.USERGUIDING_REPORT_GUIDE_ID));
      }
      window.userGuiding.getHistory().forEach((guideId) => {
        window.userGuiding.previewGuide(Number(guideId));
      });
    }
  }

  toOldProject(project: V2Project): Project {
    return {
      projectId: project.identifier,
      dateCreated: project.dateCreated,
      organization: project.orgId,
      orgChartId: "",
      name: project.name,
      projectType: project.description || "",
      theme: project.theme,
      organizationNodes: [],
      isDeleted: false,
      features: project.features || [],
      rootBenchmarkId: project.rootBenchmarkId,
    };
  }
}
